import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import TestCloseButton from "../components/TestCloseButton";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  ChartBar, ArrowRight, CheckCircle, WarningCircle, Trophy, Scales, ListNumbers,
} from "@phosphor-icons/react";

const SELECTION_COUNT = 10;

export default function CoacheeWorkValuesPage() {
  const [coachee, setCoachee] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pickedForTriple, setPickedForTriple] = useState([]); // ranked values during triple resolution

  // Selection state (pre-confirm)
  const [selectedSet, setSelectedSet] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const { data: u } = await api.get("/auth/me");
        const { data: list } = await api.get("/coachees");
        const own = list.find((p) => p.user_id === u.id) || null;
        setCoachee(own);
        if (own) {
          const { data: s } = await api.get(`/coachees/${own.id}/work-values`);
          setSession(s);
          if (s.selected_values?.length) setSelectedSet(new Set(s.selected_values));
        }
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Reset triple-picker whenever the current question changes
  useEffect(() => { setPickedForTriple([]); }, [
    session?.current_question?.kind,
    JSON.stringify(session?.current_question?.values || []),
    session?.current_question?.a,
    session?.current_question?.b,
  ]);

  const handleStart = async () => {
    if (!coachee || busy) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/coachees/${coachee.id}/work-values/start`);
      setSession(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const handleSelectionToggle = (value) => {
    setError("");
    setSelectedSet((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        if (next.size >= SELECTION_COUNT) return prev;  // cap at 10
        next.add(value);
      }
      return next;
    });
  };

  const handleSelectionSubmit = async () => {
    if (!coachee || busy || selectedSet.size !== SELECTION_COUNT) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/coachees/${coachee.id}/work-values/select`, {
        values: Array.from(selectedSet),
      });
      setSession(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const handlePairAnswer = async (winner, loser) => {
    if (!coachee || busy) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/coachees/${coachee.id}/work-values/answer`, {
        pair: [winner, loser],
      });
      setSession(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const handleTriplePick = (value) => {
    setPickedForTriple((prev) => {
      if (prev.includes(value)) return prev;
      const next = [...prev, value];
      if (next.length === 3 && coachee) {
        // submit
        (async () => {
          setBusy(true); setError("");
          try {
            const { data } = await api.post(`/coachees/${coachee.id}/work-values/answer`, {
              triple: next,
            });
            setSession(data);
          } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
          } finally { setBusy(false); }
        })();
      }
      return next;
    });
  };

  if (loading) {
    return <AppLayout title="Werkwaardetest"><div className="text-zinc-500">Laden...</div></AppLayout>;
  }

  // Result screen
  if (session?.state === "completed" && session.result) {
    return <ResultView result={session.result} />;
  }

  // Comparing — pair
  if (session?.state === "comparing" && session.current_question?.kind === "pair") {
    return (
      <PairView
        a={session.current_question.a}
        b={session.current_question.b}
        pairIndex={session.pair_index || 0}
        pairsTotal={session.pairs_total}
        onPick={handlePairAnswer}
        busy={busy}
        error={error}
      />
    );
  }

  // Comparing — triple (transitivity violation)
  if (session?.state === "comparing" && session.current_question?.kind === "triple") {
    return (
      <TripleView
        values={session.current_question.values}
        picked={pickedForTriple}
        onPick={handleTriplePick}
        onReset={() => setPickedForTriple([])}
        busy={busy}
        error={error}
      />
    );
  }

  // Selection screen
  if (session?.state === "selection") {
    return (
      <SelectionView
        allValues={session.all_values || []}
        selected={selectedSet}
        onToggle={handleSelectionToggle}
        onSubmit={handleSelectionSubmit}
        busy={busy}
        error={error}
      />
    );
  }

  // not_started — intro
  return <IntroView onStart={handleStart} busy={busy} error={error} />;
}

// ---------------------------------------------------------------------------
// Intro
// ---------------------------------------------------------------------------
function IntroView({ onStart, busy, error }) {
  return (
    <AppLayout title="Werkwaardetest" breadcrumbs={[{ label: "Tests", to: "/tests" }, { label: "Werkwaardetest" }]}>
      <div className="max-w-2xl space-y-6" data-testid="wv-intro">
        <div className="neuro-card-bold p-6 bg-white">
          <div className="flex items-start gap-3">
            <div className="shrink-0 inline-flex h-12 w-12 items-center justify-center bg-[#1E88E5]/10 text-[#1E88E5] rounded-sm">
              <ChartBar size={26} weight="duotone" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-2xl font-bold tracking-tight">Werkwaardetest</h2>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                Welke werkwaarden vind jij echt belangrijk? Eerst kies je <strong>10 waarden uit 60</strong>
                . Daarna leggen we je telkens twee waarden voor — welke vind je belangrijker?
                Daarmee bouwen we jouw persoonlijke <strong>top 10</strong>.
              </p>
              <ul className="text-sm text-zinc-600 mt-3 space-y-1.5 list-disc list-inside">
                <li>Je kunt de test rustig op je eigen tempo doen.</li>
                <li>Tussentijdse antwoorden worden automatisch bewaard.</li>
                <li>Als je antwoorden tegenstrijdig zijn, vragen we je om drie waarden op volgorde te zetten.</li>
                <li>De test kan maar één keer gemaakt worden — je work coach kan resetten.</li>
              </ul>
            </div>
          </div>
        </div>
        {error && <ErrorBox text={error} />}
        <button
          type="button"
          onClick={onStart}
          disabled={busy}
          data-testid="wv-start-btn"
          className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:bg-zinc-400"
        >
          {busy ? "Bezig..." : "Start de test"} <ArrowRight size={16} />
        </button>
      </div>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Selection screen
// ---------------------------------------------------------------------------
function SelectionView({ allValues, selected, onToggle, onSubmit, busy, error }) {
  const remaining = SELECTION_COUNT - selected.size;
  return (
    <AppLayout title="Werkwaardetest" breadcrumbs={[{ label: "Tests", to: "/tests" }, { label: "Werkwaardetest" }]}>
      <div className="max-w-4xl space-y-4" data-testid="wv-selection">
        <div className="neuro-card-bold p-5 bg-white flex items-start gap-3">
          <Trophy size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="font-display text-lg font-bold tracking-tight">Kies jouw 10 belangrijkste werkwaarden</h2>
            <p className="text-sm text-zinc-600 mt-1">
              Je hoeft niet meteen te ranken — kies eerst gewoon de tien waarden die het meest bij
              jou passen. In de volgende stappen bepalen we samen de onderlinge volgorde.
            </p>
            <div className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500">
              <span data-testid="wv-selected-count">
                <strong className={selected.size === SELECTION_COUNT ? "text-[#43A047]" : "text-zinc-900"}>
                  {selected.size}
                </strong> / {SELECTION_COUNT} geselecteerd
              </span>
              {remaining > 0 && <span className="text-zinc-400">· nog {remaining} te kiezen</span>}
            </div>
          </div>
        </div>

        {error && <ErrorBox text={error} />}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="wv-values-grid">
          {allValues.map((v) => {
            const isOn = selected.has(v);
            const disabled = !isOn && selected.size >= SELECTION_COUNT;
            return (
              <button
                key={v}
                type="button"
                onClick={() => onToggle(v)}
                disabled={disabled}
                data-testid={`wv-value-${v.replace(/\s+/g, "-").toLowerCase()}`}
                className={
                  "text-left p-3 border-2 rounded-sm transition-all text-sm font-medium " +
                  (isOn
                    ? "border-[#1E88E5] bg-[#1E88E5]/10 text-zinc-900"
                    : disabled
                      ? "border-zinc-200 bg-zinc-50 text-zinc-400 cursor-not-allowed"
                      : "border-zinc-200 hover:border-zinc-400 bg-white text-zinc-700")
                }
              >
                {v}
              </button>
            );
          })}
        </div>

        <div className="pt-2">
          <button
            type="button"
            onClick={onSubmit}
            disabled={busy || selected.size !== SELECTION_COUNT}
            data-testid="wv-selection-submit"
            className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:bg-zinc-400"
          >
            {busy ? "Bezig..." : "Bevestig selectie"} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Pair-comparison screen
// ---------------------------------------------------------------------------
function PairView({ a, b, pairIndex, pairsTotal, onPick, busy, error }) {
  const percent = pairsTotal > 0 ? Math.round((pairIndex / pairsTotal) * 100) : 0;
  return (
    <AppLayout title="Werkwaardetest">
      <div className="max-w-3xl space-y-4" data-testid="wv-pair">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs uppercase tracking-widest text-zinc-500">
            Vergelijking{" "}
            <strong className="text-zinc-900" data-testid="wv-pair-index">
              {pairIndex + 1}
            </strong>{" "}
            van {pairsTotal}
          </div>
          <div className="text-xs uppercase tracking-widest text-zinc-500">{percent}% klaar</div>
        </div>
        <div className="h-1 bg-zinc-100">
          <div className="h-full bg-[#1E88E5] transition-all duration-300" style={{ width: `${percent}%` }} />
        </div>

        <div className="neuro-card-bold p-6 bg-white">
          <div className="flex items-start gap-3 mb-4">
            <Scales size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
            <div>
              <h2 className="font-display text-lg md:text-xl font-bold tracking-tight">
                Welke baan past beter bij jou?
              </h2>
              <p className="text-sm text-zinc-600 mt-1">
                Het gaat om welke waarde voor jou zwaarder weegt — er is geen goed of fout.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            <DilemmaButton
              heavy={a}
              light={b}
              testId="wv-pair-pick-a"
              onClick={() => onPick(a, b)}
              disabled={busy}
            />
            <DilemmaButton
              heavy={b}
              light={a}
              testId="wv-pair-pick-b"
              onClick={() => onPick(b, a)}
              disabled={busy}
            />
          </div>
        </div>

        {error && <ErrorBox text={error} />}
      </div>
    </AppLayout>
  );
}

function DilemmaButton({ heavy, light, onClick, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="text-left p-5 border-2 border-zinc-200 hover:border-zinc-900 rounded-sm transition-all bg-white disabled:opacity-50 disabled:cursor-not-allowed group"
    >
      <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Een baan met</div>
      <div className="font-display text-lg font-bold tracking-tight text-zinc-900 group-hover:text-[#1E88E5] transition-colors">
        veel <strong className="underline decoration-[#1E88E5] decoration-2 underline-offset-4">{heavy}</strong>
      </div>
      <div className="text-sm text-zinc-500 mt-2">
        maar weinig <strong className="text-zinc-700">{light}</strong>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Triple-rank screen (transitivity violation)
// ---------------------------------------------------------------------------
function TripleView({ values, picked, onPick, onReset, busy, error }) {
  const remaining = values.filter((v) => !picked.includes(v));
  const next = picked.length;  // 0=eerste, 1=tweede, 2=derde
  const labels = ["belangrijkst", "op de tweede plaats", "minst belangrijk"];
  return (
    <AppLayout title="Werkwaardetest">
      <div className="max-w-3xl space-y-4" data-testid="wv-triple">
        <div className="neuro-card-bold p-6 bg-white border-amber-300">
          <div className="flex items-start gap-3">
            <WarningCircle size={26} weight="duotone" className="text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-amber-600 mb-1">
                Antwoorden zijn tegenstrijdig
              </div>
              <h2 className="font-display text-lg md:text-xl font-bold tracking-tight">
                Zet deze drie waarden op volgorde
              </h2>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                Je antwoorden zijn niet helemaal consistent met elkaar. Geen probleem — kies welke
                van deze drie het <strong>belangrijkste</strong> is, daarna de tweede en de derde.
              </p>
            </div>
          </div>
        </div>

        {picked.length > 0 && (
          <div className="neuro-card p-4 bg-white" data-testid="wv-triple-picked">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Jouw volgorde tot nu toe</div>
            <ol className="space-y-1">
              {picked.map((v, i) => (
                <li key={v} className="flex items-center gap-2 text-sm">
                  <span className="inline-flex h-5 w-5 items-center justify-center bg-zinc-900 text-white text-xs rounded-sm font-mono">
                    {i + 1}
                  </span>
                  <span className="font-medium">{v}</span>
                </li>
              ))}
            </ol>
            {picked.length < 3 && (
              <button
                type="button"
                onClick={onReset}
                data-testid="wv-triple-reset"
                className="mt-3 text-xs text-zinc-500 hover:text-zinc-900 underline"
              >
                Opnieuw kiezen
              </button>
            )}
          </div>
        )}

        {next < 3 && (
          <div className="neuro-card-bold p-5 bg-white" data-testid="wv-triple-picker">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              Stap {next + 1} van 3
            </div>
            <h3 className="font-display text-base font-bold tracking-tight">
              Welke is {labels[next]}?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
              {remaining.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => onPick(v)}
                  disabled={busy}
                  data-testid={`wv-triple-pick-${v.replace(/\s+/g, "-").toLowerCase()}`}
                  className="text-left p-4 border-2 border-zinc-200 hover:border-zinc-900 rounded-sm transition-all bg-white text-sm font-semibold disabled:opacity-50"
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && <ErrorBox text={error} />}
      </div>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Result screen
// ---------------------------------------------------------------------------
function ResultView({ result }) {
  const top = useMemo(() => result || [], [result]);
  return (
    <AppLayout title="Werkwaardetest" breadcrumbs={[{ label: "Tests", to: "/tests" }, { label: "Werkwaardetest" }]}>
      <div className="max-w-2xl space-y-6" data-testid="wv-result">
        <div className="neuro-card-bold p-6 bg-white">
          <div className="flex items-start gap-3">
            <Trophy size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Jouw top 10</div>
              <h2 className="font-display text-2xl font-bold tracking-tight">Werkwaarden­hiërarchie</h2>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                Dit zijn de tien werkwaarden die jij het belangrijkst vindt, gerangschikt op
                basis van al jouw antwoorden.
              </p>
            </div>
          </div>
        </div>

        <div className="neuro-card p-6 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <ListNumbers size={18} weight="duotone" className="text-[#1E88E5]" />
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Ranglijst</div>
          </div>
          <ol className="space-y-2" data-testid="wv-result-list">
            {top.map((v, i) => (
              <li
                key={v}
                data-testid={`wv-result-${i + 1}`}
                className={
                  "flex items-center gap-3 p-3 rounded-sm border " +
                  (i === 0
                    ? "border-[#1E88E5] bg-[#1E88E5]/5"
                    : "border-zinc-200 bg-white")
                }
              >
                <span className={
                  "inline-flex h-8 w-8 items-center justify-center text-sm font-bold rounded-sm font-mono " +
                  (i === 0 ? "bg-[#1E88E5] text-white" : "bg-zinc-100 text-zinc-700")
                }>
                  {i + 1}
                </span>
                <span className="font-medium text-zinc-900">{v}</span>
                {i === 0 && (
                  <span className="ml-auto text-[10px] uppercase tracking-widest text-[#1E88E5]">
                    Belangrijkst
                  </span>
                )}
              </li>
            ))}
          </ol>
        </div>

        <TestCloseButton testId="wv-result-back" />
      </div>
    </AppLayout>
  );
}

function ErrorBox({ text }) {
  return (
    <div
      className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm flex items-start gap-2"
      data-testid="wv-error"
    >
      <WarningCircle size={18} className="shrink-0 mt-0.5" />
      <div>{text}</div>
    </div>
  );
}
